import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  AlertTriangle,
  DollarSign,
  ShieldCheck,
  Clock,
  TrendingUp,
  TrendingDown,
  UserPlus,
  CalendarClock,
  ArrowRight,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { superFetch, formatCurrency, formatDate } from '@/lib/super-admin-api';
import type { ChartData } from '@/lib/super-admin-types';

interface DashboardMetrics {
  totalTenants: number;
  activeTenants: number;
  blockedTenants: number;
  expiredSubscriptions: number;
  monthlyRevenue: number;
  pendingRequests: number;
  newThisMonth: number;
  newLastMonth: number;
  expiringTenants: { id: string; nome_fantasia: string; subscription_expires_at: string; plan_name: string }[];
  recentTenants: { id: string; nome_fantasia: string; status: string; created_at: string; plan_name: string }[];
}

export function SuperAdminDashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [charts, setCharts] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('superAdminUser') || '{}');

  useEffect(() => {
    async function fetchData() {
      try {
        const [metricsRes, chartsRes] = await Promise.all([
          superFetch('/api/super-admin/metrics'),
          superFetch('/api/super-admin/metrics/charts'),
        ]);
        if (metricsRes.ok) setMetrics(await metricsRes.json());
        if (chartsRes.ok) setCharts(await chartsRes.json());
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const growthPercent = metrics && metrics.newLastMonth > 0
    ? Math.round(((metrics.newThisMonth - metrics.newLastMonth) / metrics.newLastMonth) * 100)
    : metrics?.newThisMonth ? 100 : 0;

  const cards = [
    { label: 'Total Estabelecimentos', value: metrics?.totalTenants ?? 0, icon: Building2, color: '#3b82f6', bg: '#3b82f615' },
    { label: 'Ativos', value: metrics?.activeTenants ?? 0, icon: ShieldCheck, color: '#10b981', bg: '#10b98115' },
    { label: 'Bloqueados', value: metrics?.blockedTenants ?? 0, icon: AlertTriangle, color: '#f59e0b', bg: '#f59e0b15' },
    { label: 'Vencidos', value: metrics?.expiredSubscriptions ?? 0, icon: Clock, color: '#ef4444', bg: '#ef444415' },
  ];

  const formatMonth = (month: string) => {
    const [, m] = month.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return months[Number(m) - 1] || month;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>
            Olá, {user?.name?.split(' ')[0] || 'Admin'}
          </h1>
          <p className="text-base mt-1" style={{ color: '#94a3b8' }}>
            Visão geral da plataforma FluxoPDV
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm" style={{ color: '#64748b' }}>
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
      </div>

      {/* Alerts Row */}
      {!loading && (
        <div className="flex flex-wrap gap-3">
          {(metrics?.pendingRequests ?? 0) > 0 && (
            <button
              onClick={() => navigate('/super-admin/requests')}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors hover:opacity-90"
              style={{ background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b30' }}
            >
              <UserPlus className="h-4 w-4" />
              {metrics!.pendingRequests} solicitação{metrics!.pendingRequests > 1 ? 'ões' : ''} pendente{metrics!.pendingRequests > 1 ? 's' : ''}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {(metrics?.expiringTenants?.length ?? 0) > 0 && (
            <div
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium"
              style={{ background: '#ef444415', color: '#ef4444', border: '1px solid #ef444425' }}
            >
              <CalendarClock className="h-4 w-4" />
              {metrics!.expiringTenants.length} tenant{metrics!.expiringTenants.length > 1 ? 's' : ''} vencendo em 7 dias
            </div>
          )}
        </div>
      )}

      {/* Revenue + Growth Row */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="animate-pulse rounded-xl p-6" style={{ background: '#1e293b' }}><div className="h-24" /></div>
          <div className="animate-pulse rounded-xl p-6" style={{ background: '#1e293b' }}><div className="h-24" /></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* MRR Card */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>Receita Mensal (MRR)</p>
                <p className="text-3xl font-bold mt-2" style={{ color: '#f1f5f9' }}>
                  {formatCurrency(metrics?.monthlyRevenue ?? 0)}
                </p>
                <p className="text-sm mt-1" style={{ color: '#64748b' }}>
                  {metrics?.activeTenants ?? 0} assinantes ativos
                </p>
              </div>
              <div className="rounded-xl p-3" style={{ background: '#10b98115' }}>
                <DollarSign className="h-7 w-7" style={{ color: '#10b981' }} />
              </div>
            </div>
          </div>

          {/* Growth Card */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>Novos este mês</p>
                <p className="text-3xl font-bold mt-2" style={{ color: '#f1f5f9' }}>
                  {metrics?.newThisMonth ?? 0}
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  {growthPercent >= 0 ? (
                    <TrendingUp className="h-4 w-4" style={{ color: '#10b981' }} />
                  ) : (
                    <TrendingDown className="h-4 w-4" style={{ color: '#ef4444' }} />
                  )}
                  <span className="text-sm font-medium" style={{ color: growthPercent >= 0 ? '#10b981' : '#ef4444' }}>
                    {growthPercent >= 0 ? '+' : ''}{growthPercent}%
                  </span>
                  <span className="text-sm" style={{ color: '#64748b' }}>vs mês anterior</span>
                </div>
              </div>
              <div className="rounded-xl p-3" style={{ background: '#3b82f615' }}>
                <TrendingUp className="h-7 w-7" style={{ color: '#3b82f6' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Cards */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl border p-4"
              style={{ background: '#1e293b', borderColor: '#334155' }}
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg p-2" style={{ background: card.bg }}>
                  <card.icon className="h-4 w-4" style={{ color: card.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: '#f1f5f9' }}>{card.value}</p>
                  <p className="text-xs" style={{ color: '#64748b' }}>{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <h3 className="text-base font-semibold mb-4" style={{ color: '#f1f5f9' }}>Receita Mensal</h3>
            {charts?.revenueByMonth && charts.revenueByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={charts.revenueByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" tickFormatter={formatMonth} stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value: number) => [formatCurrency(value), 'Receita']}
                    labelFormatter={formatMonth}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px]">
                <p className="text-sm" style={{ color: '#64748b' }}>Sem dados disponíveis</p>
              </div>
            )}
          </div>

          {/* Growth Chart */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <h3 className="text-base font-semibold mb-4" style={{ color: '#f1f5f9' }}>Crescimento de Tenants</h3>
            {charts?.tenantGrowth && charts.tenantGrowth.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={charts.tenantGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" tickFormatter={formatMonth} stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value: number, name: string) => [value, name === 'total' ? 'Total' : 'Novos']}
                    labelFormatter={formatMonth}
                  />
                  <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#3b82f620" strokeWidth={2} />
                  <Area type="monotone" dataKey="new" stroke="#10b981" fill="#10b98120" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px]">
                <p className="text-sm" style={{ color: '#64748b' }}>Sem dados disponíveis</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage by Tenant */}
      {!loading && charts?.usageByTenant && charts.usageByTenant.length > 0 && (
        <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: '#f1f5f9' }}>
            Top Estabelecimentos — Vendas (30 dias)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={charts.usageByTenant} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={12} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={130} tick={{ fill: '#94a3b8' }} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                formatter={(value: number) => [formatCurrency(value), 'Vendas']}
              />
              <Bar dataKey="sales" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bottom Row: Expiring + Recent */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expiring Soon */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: '#f1f5f9' }}>
                <CalendarClock className="h-4 w-4" style={{ color: '#ef4444' }} />
                Vencendo em 7 dias
              </h3>
            </div>
            {metrics?.expiringTenants && metrics.expiringTenants.length > 0 ? (
              <div className="space-y-3">
                {metrics.expiringTenants.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between cursor-pointer rounded-lg px-3 py-2.5 transition-colors hover:bg-white/5"
                    onClick={() => navigate(`/super-admin/tenants/${t.id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#f1f5f9' }}>{t.nome_fantasia}</p>
                      <p className="text-xs" style={{ color: '#64748b' }}>{t.plan_name}</p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded" style={{ background: '#ef444420', color: '#ef4444' }}>
                      {formatDate(t.subscription_expires_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm py-4 text-center" style={{ color: '#64748b' }}>Nenhum vencimento próximo</p>
            )}
          </div>

          {/* Recent Tenants */}
          <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: '#f1f5f9' }}>
                <Building2 className="h-4 w-4" style={{ color: '#3b82f6' }} />
                Últimos cadastrados
              </h3>
              <button
                onClick={() => navigate('/super-admin/tenants')}
                className="text-xs font-medium hover:underline"
                style={{ color: '#3b82f6' }}
              >
                Ver todos →
              </button>
            </div>
            {metrics?.recentTenants && metrics.recentTenants.length > 0 ? (
              <div className="space-y-3">
                {metrics.recentTenants.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between cursor-pointer rounded-lg px-3 py-2.5 transition-colors hover:bg-white/5"
                    onClick={() => navigate(`/super-admin/tenants/${t.id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#f1f5f9' }}>{t.nome_fantasia}</p>
                      <p className="text-xs" style={{ color: '#64748b' }}>{t.plan_name} • {formatDate(t.created_at)}</p>
                    </div>
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        background: t.status === 'ativo' ? '#10b98120' : '#64748b20',
                        color: t.status === 'ativo' ? '#10b981' : '#94a3b8',
                      }}
                    >
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm py-4 text-center" style={{ color: '#64748b' }}>Nenhum tenant cadastrado</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
